import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import DashboardLayout from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { 
  TrendingUp, Calendar, FileText, Download, Eye, ArrowLeft, Wallet,
  Clock, CheckCircle2, XCircle, AlertCircle, History, Banknote, ChevronRight, Edit
} from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Checkbox } from '../../components/ui/checkbox';
import { SignaturePad } from '../../components/ui/signature-pad';
import { useRef } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;

// Helper to get headers with mirror mode support
const getHeaders = (token) => {
  const headers = { Authorization: `Bearer ${token}` };
  const mirrorClientId = localStorage.getItem('mirrorClientId');
  if (mirrorClientId) {
    headers['X-Mirror-Client-Id'] = mirrorClientId;
  }
  return headers;
};

export default function ClientMyInvestments() {
  const navigate = useNavigate();
  const [investments, setInvestments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedInvestment, setSelectedInvestment] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [interestPayments, setInterestPayments] = useState([]);
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [totalInterestReceived, setTotalInterestReceived] = useState(0);
  const [showSignModal, setShowSignModal] = useState(null);
  const [signCity, setSignCity] = useState('');
  const [signAcceptTerms, setSignAcceptTerms] = useState(false);
  const [signAcceptAgreement, setSignAcceptAgreement] = useState(false);
  const [signing, setSigning] = useState(false);
  const signPadRef = useRef(null);
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchInvestments();
    fetchTotalInterest();
  }, []);

  const fetchInvestments = async () => {
    try {
      const response = await axios.get(`${API}/api/placements/my-investments`, {
        headers: getHeaders(token)
      });
      const investmentsData = Array.isArray(response.data)
        ? response.data
        : (response.data?.investments || response.data?.subscriptions || []);
      setInvestments(investmentsData);
    } catch (error) {
      console.error('Error fetching investments:', error);
      toast.error('Erreur lors du chargement des investissements');
    } finally {
      setLoading(false);
    }
  };

  const fetchTotalInterest = async () => {
    try {
      const response = await axios.get(`${API}/api/placements/interests/history`, {
        headers: getHeaders(token)
      });
      setTotalInterestReceived(response.data.total_interest_paid || 0);
    } catch (error) {
      console.error('Error fetching interest history:', error);
    }
  };

  const fetchInterestPayments = async (subscriptionId) => {
    setLoadingPayments(true);
    try {
      const response = await axios.get(`${API}/api/placements/interests/subscription/${subscriptionId}`, {
        headers: getHeaders(token)
      });
      setInterestPayments(response.data.payments || []);
    } catch (error) {
      console.error('Error fetching interest payments:', error);
      setInterestPayments([]);
    } finally {
      setLoadingPayments(false);
    }
  };

  const handleViewDetail = (investment) => {
    setSelectedInvestment(investment);
    setShowDetail(true);
    fetchInterestPayments(investment.id);
  };

  const downloadContract = async (investment) => {
    try {
      // Use the new endpoint that fetches correct client data and signature from subscription
      const response = await axios.get(`${API}/api/placements/subscriptions/${investment.id}/contract`, {
        headers: getHeaders(token),
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Contrat_${investment.product?.name || 'Placement'}_${investment.id.slice(0, 8)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Contrat téléchargé');
    } catch (error) {
      console.error('Error downloading contract:', error);
      toast.error('Erreur lors du téléchargement');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return <Badge className="bg-green-500"><CheckCircle2 className="w-3 h-3 mr-1" />Actif</Badge>;
      case 'pending':
        return <Badge className="bg-orange-500"><Clock className="w-3 h-3 mr-1" />En attente de validation</Badge>;
      case 'pending_signature':
        return <Badge className="bg-amber-500"><FileText className="w-3 h-3 mr-1" />À signer</Badge>;
      case 'cancelled':
        return <Badge variant="outline" className="text-red-500 border-red-500"><XCircle className="w-3 h-3 mr-1" />Annulé</Badge>;
      case 'cloture':
        return <Badge variant="outline"><AlertCircle className="w-3 h-3 mr-1" />Clôturé</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const handleSign = async () => {
    if (!showSignModal) return;
    const sigData = signPadRef.current?.getDataURL();
    if (!sigData || signPadRef.current?.isEmpty()) {
      toast.error('Veuillez signer le contrat');
      return;
    }
    if (!signCity) { toast.error('Veuillez renseigner la ville'); return; }
    if (!signAcceptTerms || !signAcceptAgreement) { toast.error('Veuillez accepter les conditions'); return; }
    
    setSigning(true);
    try {
      await axios.post(`${API}/api/placements/subscriptions/${showSignModal.id}/sign`, {
        signature_data: sigData,
        city: signCity
      }, { headers: getHeaders(token) });
      toast.success('Contrat signé — en attente de validation');
      setShowSignModal(null);
      setSignCity('');
      setSignAcceptTerms(false);
      setSignAcceptAgreement(false);
      fetchInvestments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setSigning(false);
    }
  };



  const safeInvestments = Array.isArray(investments) ? investments : [];
  const getInvestmentAmount = (investment) => (
    investment?.current_amount ?? investment?.amount ?? 0
  );

  const totalInvested = safeInvestments
    .filter(i => i.status === 'active' || i.status === 'pending' || i.status === 'pending_signature')
    .reduce((sum, i) => sum + getInvestmentAmount(i), 0);

  const totalExpectedGains = safeInvestments
    .filter(i => i.status === 'active')
    .reduce((sum, i) => sum + (i.total_interest || 0), 0);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <DashboardLayout>
    <div className="space-y-6">
      {/* Header */}
      <div>
          <div className="flex items-center gap-4">
            <div>
              <h1 className="text-2xl font-bold">Mes Investissements</h1>
              <p className="text-muted-foreground text-sm">Gérez vos placements et contrats</p>
            </div>
          </div>
      </div>

      <div className="space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 border-blue-500/20">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-full bg-blue-500/20">
                  <Wallet className="w-6 h-6 text-blue-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Investi</p>
                  <p className="text-2xl font-bold">{totalInvested.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-emerald-500/10 to-emerald-600/5 border-emerald-500/20">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-full bg-emerald-500/20">
                  <TrendingUp className="w-6 h-6 text-emerald-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Gains Attendus</p>
                  <p className="text-2xl font-bold text-emerald-500">+{totalExpectedGains.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-amber-500/10 to-amber-600/5 border-amber-500/20">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-full bg-amber-500/20">
                  <Banknote className="w-6 h-6 text-amber-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Intérêts Reçus</p>
                  <p className="text-2xl font-bold text-amber-500">+{totalInterestReceived.toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-500/10 to-purple-600/5 border-purple-500/20">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-full bg-purple-500/20">
                  <FileText className="w-6 h-6 text-purple-500" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Contrats Actifs</p>
                  <p className="text-2xl font-bold">{investments.filter(i => i.status === 'active').length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Investment List */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Mes Contrats de Placement
            </CardTitle>
          </CardHeader>
          <CardContent>
            {safeInvestments.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-16 h-16 mx-auto text-muted-foreground/50 mb-4" />
                <h3 className="text-lg font-medium mb-2">Aucun investissement</h3>
                <p className="text-muted-foreground mb-4">Vous n&apos;avez pas encore souscrit à un placement.</p>
                <Button onClick={() => navigate('/client/placements')}>
                  Découvrir les placements
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                {safeInvestments.map((investment) => (
                  <Card 
                    key={investment.id} 
                    className="bg-card/50 hover:bg-card/80 transition-colors cursor-pointer"
                    onClick={() => handleViewDetail(investment)}
                  >
                    <CardContent className="p-4">
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <h3 className="font-semibold text-lg">{investment.product_name || investment.product?.name || 'Placement'}</h3>
                            {getStatusBadge(investment.status)}
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                            <div>
                              <span className="text-muted-foreground">Montant investi</span>
                              <p className="font-medium">{getInvestmentAmount(investment).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</p>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Durée</span>
                              <p className="font-medium">{investment.duration_months || investment.product?.duration_months} mois</p>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Taux {(investment.rentability_period || investment.interest_period || 'Annuel').toLowerCase()} NET</span>
                              <p className="font-medium text-emerald-500">{investment.rentability_rate || investment.product?.rentability_rate}%</p>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Période versement</span>
                              <p className="font-medium">{investment.interest_period || 'Mensuel'}</p>
                            </div>
                            {investment.next_interest_date && investment.status === 'active' && (
                              <div>
                                <span className="text-muted-foreground">Prochain versement</span>
                                <p className="font-medium text-blue-500">{new Date(investment.next_interest_date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })}</p>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewDetail(investment);
                            }}
                            data-testid={`view-investment-${investment.id}`}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            Détails
                          </Button>
                          {(investment.status === 'active' || investment.status === 'pending') && (
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                downloadContract(investment);
                              }}
                              data-testid={`download-contract-${investment.id}`}
                            >
                              <Download className="w-4 h-4 mr-1" />
                              Contrat
                            </Button>
                          )}
                          {investment.status === 'pending_signature' && (
                            <Button 
                              size="sm"
                              className="bg-amber-500 hover:bg-amber-600 text-white"
                              onClick={(e) => {
                                e.stopPropagation();
                                setShowSignModal(investment);
                                setSignCity('');
                                setSignAcceptTerms(false);
                                setSignAcceptAgreement(false);
                              }}
                              data-testid={`sign-contract-${investment.id}`}
                            >
                              <Edit className="w-4 h-4 mr-1" />
                              Signer
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Detail Modal */}
      {showDetail && selectedInvestment && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <CardHeader className="border-b">
              <div className="flex items-center justify-between">
                <CardTitle>Détails du Placement</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setShowDetail(false)}>
                  <XCircle className="w-5 h-5" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-6 space-y-6">
              {/* Product Info */}
              <div>
                <h3 className="text-lg font-semibold mb-2">{selectedInvestment.product_name || selectedInvestment.product?.name || 'Placement'}</h3>
                {getStatusBadge(selectedInvestment.status)}
              </div>

              {/* Financial Summary */}
              <div className="grid grid-cols-3 gap-4">
                <div className="p-4 rounded-lg bg-muted/50">
                  <p className="text-sm text-muted-foreground">Montant Investi</p>
                  <p className="text-2xl font-bold">{getInvestmentAmount(selectedInvestment).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</p>
                </div>
                <div className="p-4 rounded-lg bg-emerald-500/10">
                  <p className="text-sm text-muted-foreground">Capital + Intérêts</p>
                  <p className="text-2xl font-bold text-emerald-500">{(selectedInvestment.current_amount || selectedInvestment.final_amount || selectedInvestment.amount || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</p>
                </div>
                <div className="p-4 rounded-lg bg-amber-500/10">
                  <p className="text-sm text-muted-foreground">Intérêts Reçus</p>
                  <p className="text-2xl font-bold text-amber-500">
                    +{interestPayments.reduce((sum, p) => sum + (p.amount || p.period_interest || p.monthly_interest || 0), 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
                  </p>
                </div>
              </div>

              {/* Details Grid */}
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Durée du contrat</span>
                  <p className="font-medium">{selectedInvestment.duration_months || selectedInvestment.product?.duration_months} mois</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Taux {(selectedInvestment.rentability_period || selectedInvestment.interest_period || 'Annuel').toLowerCase()} NET</span>
                  <p className="font-medium">{selectedInvestment.rentability_rate || selectedInvestment.product?.rentability_rate}%</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Période de versement</span>
                  <p className="font-medium">{selectedInvestment.interest_period || 'Mensuel'}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Total des intérêts prévus</span>
                  <p className="font-medium text-emerald-500">+{(selectedInvestment.total_interest || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Date de souscription</span>
                  <p className="font-medium">{selectedInvestment.created_at ? new Date(selectedInvestment.created_at).toLocaleDateString('fr-FR') : '-'}</p>
                </div>
                {selectedInvestment.next_interest_date && selectedInvestment.status === 'active' && (
                <div>
                  <span className="text-muted-foreground">Prochain versement</span>
                  <p className="font-medium text-blue-500">{new Date(selectedInvestment.next_interest_date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })}</p>
                </div>
                )}
                <div>
                  <span className="text-muted-foreground">Date d&apos;échéance</span>
                  <p className="font-medium">
                    {selectedInvestment.end_date 
                      ? new Date(selectedInvestment.end_date).toLocaleDateString('fr-FR')
                      : 'Non définie'}
                  </p>
                </div>
              </div>

              {/* Interest Payment History */}
              <div className="border-t pt-4">
                <div className="flex items-center gap-2 mb-4">
                  <History className="w-5 h-5 text-amber-500" />
                  <h4 className="font-semibold">Historique des Versements d&apos;Intérêts</h4>
                </div>
                
                {loadingPayments ? (
                  <div className="flex items-center justify-center py-4">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
                  </div>
                ) : interestPayments.length === 0 ? (
                  <div className="text-center py-6 bg-muted/30 rounded-lg">
                    <Banknote className="w-10 h-10 mx-auto text-muted-foreground/50 mb-2" />
                    <p className="text-muted-foreground text-sm">Aucun versement d&apos;intérêt reçu pour le moment</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Les intérêts sont versés {selectedInvestment.interest_period === 'Mensuel' ? 'chaque mois' : 
                        selectedInvestment.interest_period === 'Trimestriel' ? 'tous les 3 mois' :
                        selectedInvestment.interest_period === 'Semestriel' ? 'tous les 6 mois' :
                        selectedInvestment.interest_period === 'Annuel' ? 'chaque année' :
                        'en fin de contrat'}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {interestPayments.map((payment) => (
                      <div 
                        key={payment.id} 
                        className="flex items-center justify-between p-3 bg-muted/30 rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <div className="p-2 rounded-full bg-amber-500/20">
                            <Banknote className="w-4 h-4 text-amber-500" />
                          </div>
                          <div>
                            <p className="font-medium text-sm">
                              {payment.payment_type === 'capitalized' ? 'Capitalisation' : 'Versement'} - {payment.interest_period || 'Mensuel'}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {new Date(payment.created_at).toLocaleDateString('fr-FR', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              })}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-amber-500">
                            +{(payment.amount || payment.period_interest || payment.monthly_interest || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })} €
                          </p>
                          {payment.payment_type === 'capitalized' && (
                            <p className="text-xs text-muted-foreground">Capitalisé</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t">
                <Button 
                  className="flex-1"
                  onClick={() => downloadContract(selectedInvestment)}
                  disabled={selectedInvestment.status === 'cancelled'}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Télécharger le contrat
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => setShowDetail(false)}
                >
                  Fermer
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Sign Contract Modal */}
      <Dialog open={!!showSignModal} onOpenChange={() => setShowSignModal(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="sign-contract-modal">
          <DialogHeader>
            <DialogTitle>Signature du contrat</DialogTitle>
          </DialogHeader>
          {showSignModal && (
            <div className="space-y-4 py-4">
              {/* Recap */}
              <div className="p-4 rounded-lg border bg-muted/30 space-y-2 text-sm">
                <h3 className="font-bold text-base">{showSignModal.product_name || showSignModal.product?.name || 'Épargne'}</h3>
                <div className="grid grid-cols-2 gap-2">
                  <div><span className="text-muted-foreground">Montant :</span> <b>{showSignModal.amount?.toLocaleString('fr-FR')} €</b></div>
                  <div><span className="text-muted-foreground">Durée :</span> <b>{showSignModal.duration_months} mois</b></div>
                  <div><span className="text-muted-foreground">Rendement :</span> <b className="text-emerald-500">{showSignModal.rentability_rate}% / {showSignModal.rentability_period}</b></div>
                  <div><span className="text-muted-foreground">Versement :</span> <b>{showSignModal.interest_period}</b></div>
                  <div><span className="text-muted-foreground">Fonds :</span> <b>{showSignModal.capitalize_funds ? 'Bloqués' : 'Disponibles'}</b></div>
                  <div><span className="text-muted-foreground">Intérêts composés :</span> <b>{showSignModal.interest_compound ? 'Oui' : 'Non'}</b></div>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Ville *</Label>
                <Input value={signCity} onChange={(e) => setSignCity(e.target.value)} placeholder="Votre ville" data-testid="sign-city" />
              </div>

              <SignaturePad ref={signPadRef} width={400} height={120} label="Signature électronique *" penColor="#1a365d" backgroundColor="#ffffff" data-testid="sign-pad" />

              <div className="space-y-3">
                <label className="flex items-start gap-3 cursor-pointer">
                  <Checkbox checked={signAcceptAgreement} onCheckedChange={setSignAcceptAgreement} data-testid="sign-agreement" />
                  <span className="text-sm">Bon pour accord</span>
                </label>
                <label className="flex items-start gap-3 cursor-pointer">
                  <Checkbox checked={signAcceptTerms} onCheckedChange={setSignAcceptTerms} data-testid="sign-terms" />
                  <span className="text-sm">J&apos;accepte les Termes et Conditions</span>
                </label>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSignModal(null)}>Annuler</Button>
            <Button onClick={handleSign} disabled={signing || !signAcceptTerms || !signAcceptAgreement || !signCity} data-testid="sign-submit">
              {signing ? 'Signature...' : 'Valider et signer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </DashboardLayout>
  );
}
